import { safeJSONParse } from "./helper";

// Helper function to decode HTML entities
const decodeHTMLEntities = (text: string): string => {
  const entities: { [key: string]: string } = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&#x27;': "'",
    '&#x2F;': '/',
    '&nbsp;': ' ',
  };

  return text.replace(/&[#\w]+;/g, (entity) => entities[entity] || entity);
};

// Helper function to clean text
const cleanText = (text: string | undefined | null): string => {
  if (!text) return "";

  let cleaned = String(text)
    .replace(/_/g, " ") // Remove underscores
    .trim();

  // Decode HTML entities
  cleaned = decodeHTMLEntities(cleaned);

  return cleaned;
};

/**
 * Extracts plain text content from agent response data based on agent type
 * @param content - Raw content string from agent response
 * @param agentId - Agent identifier to determine parsing strategy
 * @returns Formatted plain text ready for copying
 */
export const extractTextContent = (
  content: string,
  agentId?: string
): string => {
  try {
    // Return raw content if it's empty or undefined
    if (!content || typeof content !== "string") {
      return "";
    }

    const parsedContent = safeJSONParse(content);
    const finalAnswer = safeJSONParse(parsedContent?.finalAnswer);

    let textContent = "";

    try {
      if (agentId === "utility_agent") {
        textContent = parsedContent?.finalAnswer?.toString() || content;
      } else if (agentId === "press_release_agent") {
        // Extract press release content
        textContent += `${finalAnswer?.payload?.headline || ""}\n\n`;
        textContent += `${finalAnswer?.payload?.subheadline || ""}\n\n`;
        textContent += `${finalAnswer?.payload?.date || ""}\n\n`;

        if (Array.isArray(finalAnswer?.payload?.body)) {
          finalAnswer.payload.body.forEach((body: string) => {
            if (body) textContent += `${body}\n\n`;
          });
        }

        // Add boiler_plate section
        if (finalAnswer?.payload?.boiler_plate) {
          const boilerPlate = finalAnswer.payload.boiler_plate;
          if (boilerPlate.title) {
            textContent += `${boilerPlate.title}\n\n`;
          }
          if (boilerPlate.body) {
            textContent += `${boilerPlate.body}\n\n`;
          }
        }

        textContent += `${finalAnswer?.payload?.media_contact || ""}\n`;
      } else if (
        agentId === "LocalizerAgent" ||
        agentId === "localizer_agent"
      ) {
        // Extract localized content
        textContent += `${finalAnswer?.payload?.headline_ar || ""}\n\n`;
        textContent += `${finalAnswer?.payload?.subheadline_ar || ""}\n\n`;
        textContent += `${finalAnswer?.payload?.date || ""}\n\n`;

        if (Array.isArray(finalAnswer?.payload?.body_ar)) {
          finalAnswer.payload.body_ar.forEach((body: string) => {
            if (body) textContent += `${body}\n\n`;
          });
        }

        textContent += `${finalAnswer?.payload?.media_contact || ""}\n`;
      } else if (agentId === "media_brief_agent") {
        const payload = finalAnswer?.payload;
        textContent += "Media Briefing: Press Release Q&A\n\n";
        textContent += "Interviewee Profile:\n";
        textContent += `${payload?.interviewee_profile || ""}\n\n`;
        textContent += "Strategic Narrative (Top-Level Messaging):\n";
        textContent += `${payload?.strategic_narrative || ""}\n\n`;
        textContent += "Key strategic points the CEO must emphasize:\n";

        if (Array.isArray(payload?.key_strategic_points)) {
          payload.key_strategic_points.forEach((point: string) => {
            if (point) textContent += `• ${point}\n`;
          });
        }

        textContent += "\nQ&A\n";
        if (Array.isArray(payload?.qa_set)) {
          payload.qa_set.forEach((qa: any, index: number) => {
            const questionNumber = qa?.question_number || index + 1;

            if (qa?.question && qa?.answer) {
              textContent += `\nQ${questionNumber}: ${qa.question}\n`;

              const riskLevel = qa.risk_level || 'LOW';

              // Handle answer based on risk level
              if (riskLevel === 'MEDIUM' || riskLevel === 'HIGH') {
                // For MEDIUM or HIGH risk
                if (Array.isArray(qa.answer)) {
                  // Answer is an array - map through each option
                  qa.answer.forEach((ans: any, ansIdx: number) => {
                    const answerText = ans.text || ans;
                    if (qa.answer.length > 1) {
                      // Multiple options: show with strategy label
                      const strategyUsed = ans.strategy_used || 'N/A';
                      textContent += `A${questionNumber} (Option ${ansIdx + 1} - ${strategyUsed}): ${answerText}\n`;
                    } else {
                      // Single option: no label needed
                      textContent += `A${questionNumber}: ${answerText}\n`;
                    }
                  });
                } else {
                  // Not an array: just show as is
                  textContent += `A${questionNumber}: ${qa.answer}\n`;
                }
              } else {
                // For LOW risk: just show first option's text
                if (Array.isArray(qa.answer)) {
                  const answerText = qa.answer[0]?.text || qa.answer[0] || qa.answer;
                  textContent += `A${questionNumber}: ${answerText}\n`;
                } else {
                  textContent += `A${questionNumber}: ${qa.answer}\n`;
                }
              }

              if (qa?.risk_level) {
                if (qa.risk_level === 'MEDIUM' || qa.risk_level === 'HIGH') {
                  textContent += `High Risk: Potentially sensitive\n`;
                } else if (qa.risk_level === 'LOW') {
                  textContent += `Low Risk\n`;
                }
              }
            }
          });
        }
      } else if (agentId === "social_media_insights_agent") {
        const twitterAnswer = safeJSONParse(parsedContent?.twitterAnswer);
        const linkedinAnswer = safeJSONParse(parsedContent?.linkedinAnswer);
        const instagramAnswer = safeJSONParse(parsedContent?.instagramAnswer);

        // Get localized content
        const socialMediaLocalizer = safeJSONParse(
          parsedContent?.socialMediaLocalizer
        );
        const twitterLocalizer = safeJSONParse(
          socialMediaLocalizer?.rawTwitterLoc
        );
        const linkedinLocalizer = safeJSONParse(
          socialMediaLocalizer?.rawLinkedinLoc
        );
        const instagramLocalizer = safeJSONParse(
          socialMediaLocalizer?.rawInstagramLoc
        );

        // Twitter Posts - only add header if posts exist
        if (Array.isArray(twitterAnswer?.payload?.posts) && twitterAnswer.payload.posts.length > 0) {
          textContent += "Twitter Posts:\n\n";
          twitterAnswer.payload.posts.forEach((post: any, idx: number) => {
            if (post) {
              textContent += `POST ${post?.option_number || ""}: ${post?.focus_area || ""}\n`;
              textContent += `${post?.text || ""}\n`;
              if (post?.read_more) {
                textContent += `${post.read_more}\n`;
              }
              textContent += `${
                Array.isArray(post?.hashtags) ? post.hashtags.join(" ") : ""
              }\n`;

              // Add localized content if available
              const localizedText =
                twitterLocalizer?.payload?.platforms?.[0]?.posts?.[idx]?.text;
              if (localizedText) {
                textContent += `\nArabic Translation:\n${localizedText}\n`;
              }
              textContent += `\n`;
            }
          });
        }

        // LinkedIn Posts - only add header if posts exist
        if (Array.isArray(linkedinAnswer?.payload?.posts) && linkedinAnswer.payload.posts.length > 0) {
          textContent += "\nLinkedIn Posts:\n\n";
          linkedinAnswer.payload.posts.forEach((post: any, idx: number) => {
            if (post) {
              textContent += `POST ${post?.option_number || ""}: ${post?.focus_area || ""}\n`;
              textContent += `${post?.text || ""}\n`;
              if (post?.read_more) {
                textContent += `${post.read_more}\n`;
              }
              textContent += `${
                Array.isArray(post?.hashtags) ? post.hashtags.join(" ") : ""
              }\n`;

              // Add localized content if available
              const localizedText =
                linkedinLocalizer?.payload?.platforms?.[0]?.posts?.[idx]?.text;
              if (localizedText) {
                textContent += `\nArabic Translation:\n${localizedText}\n`;
              }
              textContent += `\n`;
            }
          });
        }

        // Instagram Posts - only add header if posts exist
        if (Array.isArray(instagramAnswer?.payload?.posts) && instagramAnswer.payload.posts.length > 0) {
          textContent += "\nInstagram Posts:\n\n";
          instagramAnswer.payload.posts.forEach((post: any, idx: number) => {
            if (post) {
              textContent += `POST ${post?.option_number || ""}: ${post?.focus_area || ""}\n`;
              textContent += `${post?.text || ""}\n`;
              if (post?.read_more) {
                textContent += `${post.read_more}\n`;
              }
              textContent += `${
                Array.isArray(post?.hashtags) ? post.hashtags.join(" ") : ""
              }\n`;

              // Add localized content if available
              const localizedText =
                instagramLocalizer?.payload?.platforms?.[0]?.posts?.[idx]?.text;
              if (localizedText) {
                textContent += `\nArabic Translation:\n${localizedText}\n`;
              }
              textContent += `\n`;
            }
          });
        }
      } else if (agentId === "internal_communication_agent") {
        const email = finalAnswer?.payload?.email;
        textContent += `Subject: ${email?.subject || ""}\n\n`;
        textContent += `${email?.greeting || ""}\n\n`;

        if (Array.isArray(email?.body_paragraphs)) {
          email.body_paragraphs.forEach((body: string) => {
            if (body) textContent += `${body}\n\n`;
          });
        }

        textContent += `${email?.closing || ""}\n`;
        textContent += `${email?.signature_block || ""}\n`;
      } else if (
        agentId === "synthetic_resume_agent" ||
        agentId === "skill_inference_agent" ||
        agentId === "jd_composer_agent" ||
        agentId === "modify_requirements" ||
        agentId === "jd_details_agent"
      ) {
        // Parse the answer field first
        const answer = safeJSONParse(parsedContent?.answer);

        // Check if data is inside an 'output' field (HR workflow format)
        let outputData = answer;
        if (typeof parsedContent?.output === "string") {
          outputData = safeJSONParse(parsedContent.output);
        } else if (parsedContent?.output && typeof parsedContent.output === "object") {
          outputData = parsedContent.output;
        }

        // Parse skill_inference_agent or jd_composer_agent data from answer or output
        let skillInferenceData: any = {};
        let isSkillInferenceWorkflow = false; // Detect if this is from Skill Inference Agent workflow

        if (typeof answer?.skill_inference_agent === "string") {
          skillInferenceData = safeJSONParse(answer.skill_inference_agent);
          isSkillInferenceWorkflow = true;
        } else if (typeof outputData?.skill_inference_agent === "string") {
          skillInferenceData = safeJSONParse(outputData.skill_inference_agent);
          isSkillInferenceWorkflow = true;
        } else if (answer?.skill_inference_agent) {
          skillInferenceData = answer.skill_inference_agent;
          isSkillInferenceWorkflow = true;
        } else if (outputData?.skill_inference_agent) {
          skillInferenceData = outputData.skill_inference_agent;
          isSkillInferenceWorkflow = true;
        } else if (typeof answer?.jd_composer_agent === "string") {
          skillInferenceData = safeJSONParse(answer.jd_composer_agent);
        } else if (typeof outputData?.jd_composer_agent === "string") {
          skillInferenceData = safeJSONParse(outputData.jd_composer_agent);
        } else if (answer?.jd_composer_agent) {
          skillInferenceData = answer.jd_composer_agent;
        } else if (outputData?.jd_composer_agent) {
          skillInferenceData = outputData.jd_composer_agent;
        }

        // Parse jd_details_agent data (contains overview and responsibilities) from answer or output
        let jdDetailsData: any = {};
        if (typeof answer?.jd_details_agent === "string") {
          jdDetailsData = safeJSONParse(answer.jd_details_agent);
        } else if (typeof outputData?.jd_details_agent === "string") {
          jdDetailsData = safeJSONParse(outputData.jd_details_agent);
        } else if (answer?.jd_details_agent) {
          jdDetailsData = answer.jd_details_agent;
        } else if (outputData?.jd_details_agent) {
          jdDetailsData = outputData.jd_details_agent;
        }

        // Get all fields from jd_details_agent
        const overview = jdDetailsData?.overview || answer?.overview;
        const responsibilities = jdDetailsData?.responsibilities || answer?.responsibilities;
        const job_title = jdDetailsData?.job_title || answer?.job_title;
        const location = jdDetailsData?.location || answer?.location;
        const about_organization = jdDetailsData?.about_organization || answer?.about_organization;
        const qualifications = jdDetailsData?.qualifications || answer?.qualifications;
        const what_we_look_for = jdDetailsData?.what_we_look_for || answer?.what_we_look_for;
        const why_g42 = jdDetailsData?.why_g42 || answer?.why_g42;
        const application = jdDetailsData?.application || answer?.application;

        // Merge skills data - use skillInferenceData if available, otherwise fallback to answer
        const mergedData = {
          ...(Object.keys(skillInferenceData).length > 0 ? skillInferenceData : answer),
          overview: overview,
          responsibilities: responsibilities,
          job_title: job_title,
          location: location,
          about_organization: about_organization,
          qualifications: qualifications,
          what_we_look_for: what_we_look_for,
          why_g42: why_g42,
          application: application,
        };

        // Job Title and Location
        if (mergedData?.job_title) {
          textContent += `Job Title: ${mergedData.job_title}\n`;
        }
        if (mergedData?.location) {
          textContent += `Location: ${mergedData.location}\n`;
          textContent += "\n";
        }

        // About the Organization
        if (mergedData?.about_organization) {
          textContent += "About the Organization:\n";
          textContent += `${mergedData.about_organization}\n\n`;
        }

        // Overview - shown after user confirms synthetic profile
        if (mergedData?.overview) {
          textContent += "Overview:\n";
          if (Array.isArray(mergedData.overview)) {
            mergedData.overview.forEach((item: string) => {
              textContent += `${item}\n`;
            });
          } else {
            textContent += `${mergedData.overview}\n`;
          }
          textContent += "\n";
        }

        // Responsibilities
        if (Array.isArray(mergedData?.responsibilities) && mergedData.responsibilities.length > 0) {
          textContent += "Responsibilities:\n";
          mergedData.responsibilities.forEach((responsibility: string) => {
            textContent += `  • ${responsibility}\n`;
          });
          textContent += "\n";
        }

        // Requirements (from qualifications field)
        if (Array.isArray(mergedData?.qualifications) && mergedData.qualifications.length > 0) {
          textContent += "Requirements:\n";
          mergedData.qualifications.forEach((qualification: string) => {
            textContent += `  • ${qualification}\n`;
          });
          textContent += "\n";
        }

        // Functional/Technical Skills
        if (Array.isArray(mergedData?.functional_skills) && mergedData.functional_skills.length > 0) {
          textContent += "FUNCTIONAL / TECHNICAL SKILLS\n";
          textContent += "-".repeat(60) + "\n";
          textContent += "Core Technical Skills:\n";
          mergedData.functional_skills.forEach((skill: string) => {
            textContent += `  • ${skill}\n`;
          });
          textContent += "\n";
        }

        // Future Skills
        if (Array.isArray(mergedData?.future_skills) && mergedData.future_skills.length > 0) {
          textContent += "Future Ready Skills:\n";
          mergedData.future_skills.forEach((skill: string) => {
            textContent += `  • ${skill}\n`;
          });
          textContent += "\n";
        }

        // Required Qualifications - Hidden only for Skill Inference Agent
        if (!isSkillInferenceWorkflow && agentId !== "skill_inference_agent" && mergedData?.required_qualifications) {
          textContent += "REQUIRED QUALIFICATIONS\n";
          textContent += "-".repeat(60) + "\n";
          if (mergedData.required_qualifications.education) {
            textContent += `  Education: ${mergedData.required_qualifications.education}\n`;
          }
          if (mergedData.required_qualifications.experience !== undefined) {
            textContent += `  Experience: ${mergedData.required_qualifications.experience}\n`;
          }
          if (mergedData.required_qualifications.location) {
            textContent += `  Location: ${mergedData.required_qualifications.location}\n`;
          }
          if (mergedData.required_qualifications.previous_company) {
            textContent += `  Previous Company: ${mergedData.required_qualifications.previous_company}\n`;
          }
          textContent += "\n";
        }

        // Preferred Qualifications - Array format - Hidden only for Skill Inference Agent
        if (
          !isSkillInferenceWorkflow &&
          agentId !== "skill_inference_agent" &&
          Array.isArray(mergedData?.preferred_qualifications) &&
          mergedData.preferred_qualifications.length > 0
        ) {
          textContent += "PREFERRED QUALIFICATIONS\n";
          textContent += "-".repeat(60) + "\n";
          mergedData.preferred_qualifications.forEach((item: any) => {
            textContent += `  • ${item.entity_type}: ${item.entity_value}\n`;
          });
          textContent += "\n";
        }

        // Scoring Weights (if available)
        if (mergedData?.scoring_weights && Object.keys(mergedData.scoring_weights).length > 0) {
          textContent += "SCORING WEIGHTS\n";
          textContent += "-".repeat(60) + "\n";
          Object.entries(mergedData.scoring_weights).forEach(([key, value]) => {
            const formattedKey = String(key).replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase());
            textContent += `  ${formattedKey}: ${value}\n`;
          });
          textContent += "\n";
        }

        // What We Look For
        if (mergedData?.what_we_look_for) {
          textContent += "What We Look For:\n";
          textContent += `${mergedData.what_we_look_for}\n\n`;
        }

        // What working at G42 offers
        if (mergedData?.why_g42) {
          textContent += "What working at G42 offers:\n";
          if (Array.isArray(mergedData.why_g42)) {
            mergedData.why_g42.forEach((item: any) => {
              if (item.type && item.value) {
                textContent += `${item.type}: ${item.value}\n`;
              } else {
                textContent += `${item}\n`;
              }
            });
          } else {
            textContent += `${mergedData.why_g42}\n`;
          }
          textContent += "\n";
        }

        // Application
        if (mergedData?.application) {
          textContent += "Application:\n";
          textContent += `${mergedData.application}\n`;
        }
      } else if (agentId === "resume_fetcher_agent") {
        const answer = safeJSONParse(parsedContent?.answer);
        if (answer?.candidates && answer.candidates.length > 0) {
          textContent += `A total of ${answer.candidates.length} qualified candidates have been identified.\n`;
        }
      } else if (agentId === "scoring_agent") {
        const answer = safeJSONParse(parsedContent?.answer);
        if (Array.isArray(answer?.top_candidates)) {
          textContent += "CANDIDATE EVALUATION\n\n";
          textContent += "Selection Criteria:\n";
          textContent += "- Alignment with functional and technical requirements\n";
          textContent += "- Strength of preferred qualification scores\n";
          textContent += "- Positive historical interview feedback\n";
          textContent += "- Current role, company, and location\n";
          textContent += "- Evidence from first-round interview responses\n\n";
          textContent += "=".repeat(70) + "\n\n";

          answer.top_candidates.forEach((candidate: any, idx: number) => {
            textContent += `CANDIDATE ${idx + 1}: ${cleanText(candidate.name)}\n`;
            textContent += "=".repeat(70) + "\n\n";

            // Employment & Location
            textContent += "CURRENT EMPLOYMENT & LOCATION\n";
            textContent += "-".repeat(70) + "\n";
            textContent += `Title: ${cleanText(candidate.current_title || "N/A")}\n`;
            textContent += `Company: ${cleanText(candidate.current_company || "N/A")}\n`;
            textContent += `Location: ${cleanText(candidate.current_location || "N/A")}\n`;
            if (candidate.linkedin_url) {
              textContent += `LinkedIn: ${cleanText(candidate.linkedin_url)}\n`;
            }
            textContent += "\n";

            // Required Qualifications
            const reqQual = candidate.skills_match?.required_qualification || {};
            const firstCandidate = answer.top_candidates[0];
            const baseReqQual = firstCandidate?.skills_match?.required_qualification || {};

            if (baseReqQual.education || baseReqQual.location || baseReqQual.experience !== undefined) {
              textContent += "REQUIRED QUALIFICATIONS\n";
              textContent += "-".repeat(70) + "\n";

              if (baseReqQual.education) {
                const status = reqQual.is_education_qualified?.toLowerCase() === "yes" ? "YES" : "NO";
                textContent += `Education: ${cleanText(baseReqQual.education)}\n`;
                textContent += `  Status: ${status}\n`;
              }

              if (baseReqQual.location) {
                const status = reqQual.is_location_qualified?.toLowerCase() === "yes" ? "YES" : "NO";
                textContent += `Location: ${cleanText(baseReqQual.location)}\n`;
                textContent += `  Status: ${status}\n`;
              }

              if (baseReqQual.experience !== undefined) {
                const status = reqQual.is_experience_qualified?.toLowerCase() === "yes" ? "YES" : "NO";
                textContent += `Experience: ${cleanText(baseReqQual.experience)}\n`;
                textContent += `  Status: ${status}\n`;
              }

              const reqMet = [
                reqQual.is_education_qualified?.toLowerCase() === "yes",
                reqQual.is_location_qualified?.toLowerCase() === "yes",
                reqQual.is_experience_qualified?.toLowerCase() === "yes"
              ].filter(Boolean).length;
              const reqTotal = 3;
              if (reqMet === reqTotal) {
                textContent += `Overall: EXCELLENT\n`;
              }
              textContent += "\n";
            }

            // Skills & Qualifications Fit
            if (candidate.search_score !== undefined ||
                (candidate.skills_match?.functional_skills && candidate.skills_match.functional_skills.length > 0) ||
                (candidate.skills_match?.preferred_qualification && candidate.skills_match.preferred_qualification.length > 0)) {

              textContent += "SKILLS AND PREFERRED QUALIFICATIONS FIT\n";
              textContent += "-".repeat(70) + "\n";

              if (candidate.search_score !== undefined) {
                const percentage = Number(candidate.search_score).toFixed(2);
                const percentageNum = Number(candidate.search_score);
                let matchLevel = percentageNum >= 80 ? "EXCELLENT" : percentageNum >= 60 ? "MODERATE" : "BASIC";
                textContent += `Overall Match Score: ${matchLevel} (${percentage}%)\n\n`;
              }

              // Functional/Technical Skills
              if (candidate.skills_match?.functional_skills && candidate.skills_match.functional_skills.length > 0) {
                textContent += "Functional/Technical Skills:\n";
                candidate.skills_match.functional_skills.forEach((skill: any) => {
                  const status = skill.result?.toLowerCase() === "yes" ? "YES" : "NO";
                  textContent += `  [${status}] ${cleanText(skill.skill)}\n`;
                });
                textContent += "\n";
              }

              // Preferred Qualifications
              if (candidate.skills_match?.preferred_qualification && candidate.skills_match.preferred_qualification.length > 0) {
                textContent += "Preferred Qualifications:\n";
                candidate.skills_match.preferred_qualification.forEach((pref: any) => {
                  const status = pref.result?.toLowerCase() === "yes" ? "YES" : "NO";
                  textContent += `  [${status}] ${cleanText(pref.entity_type)}: ${cleanText(pref.entity_value || "N/A")}\n`;
                  if (pref.resoning) {
                    textContent += `        ${cleanText(pref.resoning)}\n`;
                  }
                });
                textContent += "\n";
              }
            }

            // Past Interview Feedback
            if (candidate.hiring_recommendation || candidate.interview_feedback) {
              textContent += "PAST INTERVIEW FEEDBACK\n";
              textContent += "-".repeat(70) + "\n";

              if (candidate.hiring_recommendation) {
                let recommendation = cleanText(candidate.hiring_recommendation);
                textContent += `Recommendation: ${recommendation.toUpperCase()}\n`;
              }

              if (candidate.interview_feedback && candidate.interview_feedback.trim()) {
                textContent += `Feedback: ${cleanText(candidate.interview_feedback)}\n`;
              }
              textContent += "\n";
            }

            // Screening Questions
            if (candidate.resume_mining_answers && candidate.resume_mining_answers.length > 0) {
              textContent += "FIRST-INTERVIEW SCREENING QUESTIONS\n";
              textContent += "-".repeat(70) + "\n";

              const answeredYes = candidate.resume_mining_answers.filter(
                (qa: any) => qa.answer?.toLowerCase() === "yes"
              ).length;
              const total = candidate.resume_mining_answers.length;
              const percentage = total > 0 ? Math.round((answeredYes / total) * 100) : 0;

              if (percentage >= 60) {
                const level = percentage >= 80 ? "EXCELLENT" : "MODERATE";
                textContent += `Overall: ${level} (${answeredYes} of ${total} positive)\n\n`;
              }

              candidate.resume_mining_answers.forEach((qa: any, qIdx: number) => {
                const answerValue = qa.answer?.toLowerCase() === "yes" ? "YES" : "NO";
                textContent += `Q${qIdx + 1}. ${cleanText(qa.question)}\n`;
                textContent += `    Answer: ${answerValue}\n`;
                if (qa.reasoning && qa.reasoning.trim()) {
                  textContent += `    Reasoning: ${cleanText(qa.reasoning)}\n`;
                }
                textContent += "\n";
              });
            }

            textContent += "\n";
          });
        } else {
          textContent = "No candidates found";
        }
      } else if (agentId === "email_drafting") {
        const answer = safeJSONParse(parsedContent?.answer);
        textContent += `${answer?.title || ""}\n\n`;
        textContent += `Selected Candidates: ${answer?.selected_candidates_count || 0}\n\n`;

        // Helper to clean HTML and remove stray symbols
        const cleanHtmlText = (html: string): string => {
          if (!html) return "";
          const tmp = document.createElement("div");
          tmp.innerHTML = html;

          // Find all anchor tags and replace with their URLs
          const anchors = tmp.querySelectorAll("a");
          anchors.forEach((anchor) => {
            const href = anchor.getAttribute("href");
            if (href) {
              const textNode = document.createTextNode(href);
              anchor.parentNode?.replaceChild(textNode, anchor);
            }
          });

          let text = tmp.textContent || tmp.innerText || "";

          // Remove stray < and > symbols
          text = text.replace(/<(?![a-zA-Z\/!])/g, '');
          text = text.replace(/(?<![a-zA-Z0-9"'])>/g, '');
          text = text.replace(/<\s*>/g, '');

          return text;
        };

        if (Array.isArray(answer?.draft_emails)) {
          answer.draft_emails.forEach((email: any) => {
            textContent += `---\nCandidate: ${email.candidate_name}\n`;
            textContent += `Email: ${email.email}\n\n`;
            textContent += `Subject: ${email.subject}\n\n`;
            textContent += `${email.greeting}\n\n`;
            if (Array.isArray(email.body_paragraphs)) {
              email.body_paragraphs.forEach((p: string) => {
                // Clean HTML and remove stray symbols from body paragraphs
                const cleanedParagraph = cleanHtmlText(p);
                textContent += `${cleanedParagraph}\n\n`;
              });
            }
            textContent += `${email.closing}\n`;
            textContent += `${email.signature_block}\n\n`;
          });
        }
      } else if (agentId === "hr_agent_v2_hitl") {
        const answer = parsedContent?.answer;
        textContent = answer || content;
      } else if (agentId === "workflow_end") {
        textContent += `Agents invoked: ${finalAnswer?.agents_invoked || ""}\n`;
        textContent += `Total time: ${
          finalAnswer?.total_time_minutes || ""
        } mins\n`;
        textContent += `Summary: ${finalAnswer?.total_summary || ""}\n`;
      } else if (agentId === "translation_agent") {
        // Extract markdown text and remove HTML mark tags
        const markdownText = finalAnswer?.text || "";
        // Remove <mark> tags and their attributes
        let cleanedText = markdownText.replace(/<mark[^>]*>(.*?)<\/mark>/g, "$1");

        // Convert markdown formatting to plain text with proper formatting
        // Headers
        cleanedText = cleanedText.replace(/^### (.*?)$/gm, "\n$1\n");
        cleanedText = cleanedText.replace(/^## (.*?)$/gm, "\n$1\n");
        cleanedText = cleanedText.replace(/^# (.*?)$/gm, "\n$1\n");

        // Bold text
        cleanedText = cleanedText.replace(/\*\*(.*?)\*\*/g, "$1");

        // Remove extra newlines but preserve paragraph structure
        cleanedText = cleanedText.replace(/\n\n\n+/g, "\n\n");

        textContent = cleanedText.trim();
      } else if (agentId === "redrafter_orchestrator") {
        // Extract redrafted contract and strip markdown formatting for clean document
        const markdownContent = finalAnswer?.result?.redrafted_contract || finalAnswer?.redrafted_contract || "";

        if (markdownContent) {
          // Strip markdown formatting for clean text
          let cleanedText = markdownContent;

          // Remove headers but preserve the text exactly
          cleanedText = cleanedText.replace(/^#{1,6}\s+/gm, "");

          // Remove bold - use non-greedy matching to avoid issues
          cleanedText = cleanedText.replace(/\*\*([^\*]+)\*\*/g, "$1");
          cleanedText = cleanedText.replace(/__([^_]+)__/g, "$1");

          // Remove italic - use non-greedy matching
          cleanedText = cleanedText.replace(/\*([^\*\n]+)\*/g, "$1");
          cleanedText = cleanedText.replace(/_([^_\n]+)_/g, "$1");

          // Remove links but keep the text exactly
          cleanedText = cleanedText.replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1");

          // Remove inline code
          cleanedText = cleanedText.replace(/`([^`]+)`/g, "$1");

          // Remove code blocks
          cleanedText = cleanedText.replace(/```[\s\S]*?```/g, "");

          // Remove horizontal rules
          cleanedText = cleanedText.replace(/^[\-\*_]{3,}$/gm, "");

          // Remove blockquotes
          cleanedText = cleanedText.replace(/^>\s*/gm, "");

          // Remove list markers
          cleanedText = cleanedText.replace(/^[\*\-\+]\s+/gm, "");
          cleanedText = cleanedText.replace(/^\d+\.\s+/gm, "");

          // Clean up excessive newlines but preserve structure
          cleanedText = cleanedText.replace(/\n{3,}/g, "\n\n");

          // Trim
          cleanedText = cleanedText.trim();

          textContent = cleanedText;
        }
      } else {
        // Default fallback - return original content
        textContent = content;
      }
    } catch (innerError) {
      console.warn("Error extracting specific agent content:", innerError);
      // Fallback to raw content
      textContent = content;
    }

    // Final fallback - if textContent is empty or only whitespace, return original
    const trimmedContent = textContent.trim();
    return trimmedContent || content;
  } catch (error) {
    console.error("Error in extractTextContent:", error);
    // Ultimate fallback - return original content
    return content || "";
  }
};

/**
 * Copies text content to clipboard with fallback for older browsers
 * @param text - Text content to copy
 * @returns Promise that resolves to true if successful, false otherwise
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (!text || typeof text !== "string") {
      console.warn("Copy failed: Invalid text content");
      return false;
    }

    // Check if clipboard API is available
    if (!navigator.clipboard) {
      console.warn("Clipboard API not available, using fallback");
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        console.error("Fallback copy failed:", err);
        document.body.removeChild(textArea);
        return false;
      }
    }

    // Use modern clipboard API
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);
    return false;
  }
};

/**
 * Main function to copy agent response content to clipboard
 * @param content - Raw content from agent response
 * @param agentId - Agent identifier
 * @returns Promise that resolves to true if successful, false otherwise
 */
export const copyAgentContent = async (
  content: string,
  agentId?: string
): Promise<boolean> => {
  try {
    // Validate inputs
    if (!content || typeof content !== "string") {
      console.warn("Copy failed: Invalid content");
      return false;
    }

    // Extract text content with fallback
    const textToCopy = extractTextContent(content, agentId);

    if (!textToCopy) {
      console.warn("Copy failed: Extracted text is empty");
      return false;
    }

    // Copy to clipboard
    return await copyToClipboard(textToCopy);
  } catch (error) {
    console.error("Failed to copy agent content:", error);
    return false;
  }
};
